import { CountryCode, GUEST_ORDER_QUERY_KEY, SUBSHIP_TYPE, type SubshipTypeValue } from '@constants'
import { OMNI_SEGMENT_TRACKING_PARAMS_KEY } from '@dni-analysis/core/utils'
import { getQueryStringFromReferrer, parseQueryStringToObject } from '@dni-analysis/events/utils'
import { WP_ORDER_STATUS } from '@hooks-api/types/dataModel/eCommerceOrders'
import { getParsedStorageItem, setStorageItem, STORAGE_EVENT } from '@utils/storageHelper'
import { newURL } from '@utils/url'
import { match } from 'path-to-regexp'
import { SPECIAL_LOGIN_SUBSHIP_TYPE_STORAGE_KEY, SUBSHIP_TYPE_TO_LIST_PATH } from '../components/cosign/login/constants'
import { getSpecialLoginParams } from './getSpecialLoginParams'
import { wordPressHost } from './location'

const appendNoCache = (baseUrl: string): string => {
  const url = new URL(baseUrl)
  url.searchParams.set('no-cache', '')
  return url.toString()
}

const withGuestOrderParam = (redirectUrl: string): string => {
  const guestOrder = new URLSearchParams(window.location.search).get(GUEST_ORDER_QUERY_KEY)
  if (!guestOrder) return redirectUrl
  const url = newURL(redirectUrl)
  if (!url) return redirectUrl
  url.searchParams.set(GUEST_ORDER_QUERY_KEY, guestOrder)
  return url.toString()
}

export const wordPressNavigate = (path: string) => {
  if (path.startsWith('/')) window.location.href = `${wordPressHost}${path}`
  else window.location.href = `${window.location.href}/${path}`
}

const AllowedCountryCodes: string[] = Object.values(CountryCode)

const matchWordPressHost = (pathname: string) => match('{/:countryCode}/*path')(pathname)
const matchPasswordReferrer = (pathname: string) => match('{/:countryCode}/password/*path')(pathname)

const getValidCountryCode = (countryCode: string | string[] | undefined): string => {
  if (typeof countryCode === 'string' && AllowedCountryCodes.includes(countryCode.toUpperCase())) {
    return countryCode.toUpperCase()
  }
  return CountryCode.TW
}

const getBaseRegisterOrLoginRedirectUrl = () => {
  const queryParamsString = getQueryStringFromReferrer(document.referrer)
  const queryParamsObject = parseQueryStringToObject(queryParamsString || '')
  setStorageItem(OMNI_SEGMENT_TRACKING_PARAMS_KEY, { paramsString: queryParamsString, paramsObject: queryParamsObject }, STORAGE_EVENT.sessionStorage)

  try {
    const referrer = appendNoCache(document.referrer)
    const referrerUrl = newURL(referrer)
    if (!referrerUrl) throw new Error('Invalid referrer')
    const referrerSite = referrerUrl.origin
    const referrerPathname = referrerUrl.pathname
    /*
    NOTE: 判斷 referrer 和目前 domain site 是否相等
    */
    if (referrerSite !== window.location.origin) {
      return appendNoCache(`${wordPressHost}/my-account/`)
    }
    /*
    NOTE: 判斷 referrer 和目前 domain countryCode 是否相等
    */
    const matchedCurrentHost = matchWordPressHost(window.location.pathname)
    const matchedReferrerSite = matchWordPressHost(referrerPathname)
    const currentCountryCode = getValidCountryCode(matchedCurrentHost ? matchedCurrentHost.params.countryCode : undefined)
    const referrerCountryCode = getValidCountryCode(matchedReferrerSite ? matchedReferrerSite.params.countryCode : undefined)

    if (currentCountryCode !== referrerCountryCode) {
      return appendNoCache(`${wordPressHost}/my-account/`)
    }

    /*
    NOTE: 避免使用者重設密碼後，重新導向回重設密碼的頁面
    */
    if (matchPasswordReferrer(referrerPathname)) {
      return appendNoCache(`${wordPressHost}/my-account/`)
    }
    return referrer
  }
  catch (error) {
    console.error(error)
    return appendNoCache(`${wordPressHost}/my-account/`)
  }
}

export const isValidSubshipType = (value: string): value is SubshipTypeValue =>
  (Object.values(SUBSHIP_TYPE) as string[]).includes(value)

export const getSubshipOrderListUrl = (subshipType: SubshipTypeValue) => {
  const listPath = SUBSHIP_TYPE_TO_LIST_PATH[subshipType]
  return appendNoCache(`${wordPressHost}/my-account/${listPath}?tabValue=${WP_ORDER_STATUS.PENDING}`)
}

export const getRegisterOrLoginRedirectUrl = () => {
  const { orderId } = getSpecialLoginParams()
  if (orderId) {
    const stored = getParsedStorageItem<{ orderId: string, subshipType: string }>(SPECIAL_LOGIN_SUBSHIP_TYPE_STORAGE_KEY, STORAGE_EVENT.sessionStorage)
    if (stored?.orderId === orderId && isValidSubshipType(stored.subshipType)) {
      return getSubshipOrderListUrl(stored.subshipType)
    }
  }

  const guestOrder = new URLSearchParams(window.location.search).get(GUEST_ORDER_QUERY_KEY)
  const baseUrl = guestOrder ? appendNoCache(`${wordPressHost}/my-account/`) : getBaseRegisterOrLoginRedirectUrl()
  return withGuestOrderParam(baseUrl)
}
